import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Clock, Calendar, Users, MapPin, Wand2, Printer, Timer,
  CheckCircle, AlertCircle, Star, Zap, MessageSquare, CalendarPlus, LifeBuoy, RefreshCw
} from "lucide-react";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";
import { useWeddingPackageName } from "@/hooks/useWeddingPackage";
import { isAppBuild, openExternal } from "@/lib/net";
import {
  isCoupleRole,
  escapeHtml,
  isPointInTime,
  formatEventTime,
  formatDuration,
  sortEventsByTime,
  coupleVisibleEvents,
  humanizeSlug,
  meaningfulText,
  categoryLabel,
  timelineMismatch,
  weddingDateText,
  weddingSlotText,
  buildWeddingIcs,
} from "@/lib/timelineDisplay";
import type { Wedding } from "@shared/schema";

interface AITimelineGeneratorProps {
  wedding: Wedding;
}

interface TimelineEvent {
  id: number;
  eventTitle: string;
  eventDescription?: string | null;
  startTime: string;
  endTime: string;
  duration: number;
  location: string;
  category: string;
  priority: 'high' | 'medium' | 'low';
  requiredStaff: string[] | null;
  equipmentNeeded: string[] | null;
  vendorInstructions: string | null;
  familyInstructions: string | null;
  contingencyPlan: string | null;
  status: string;
  sortOrder?: number | null;
}

const EMPTY_TIMELINE = { timeline: null, events: [], staffAssignments: [] };

/** Same rule as the server (E.164, 8-15 digits, no leading 0). */
function isValidSharePhone(raw: string): boolean {
  const t = raw.trim();
  if (!/^\+?[\d\s\-().]+$/.test(t)) return false;
  let digits = t.replace(/[^\d]/g, "");
  if (!t.startsWith("+") && digits.startsWith("00")) digits = digits.slice(2);
  return /^[1-9]\d{7,14}$/.test(digits);
}

export function AITimelineGenerator({ wedding }: AITimelineGeneratorProps) {
  const { user } = useAuth();
  // Couples see a clean, guest-facing schedule; staff (admin/event manager pages that
  // render this component) keep the full run sheet.
  const coupleView = isCoupleRole(user?.role);
  const [activeTab, setActiveTab] = useState("generate");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharePhoneNumber, setSharePhoneNumber] = useState("");
  const [sharePhoneError, setSharePhoneError] = useState<string | null>(null);
  // Staff-only "Regenerate from booking" preview (review H52). Never used in the couple view.
  const [regeneratePreview, setRegeneratePreview] = useState<any | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch existing timeline. "No timeline yet" = 200 {timeline: null} (or an older server's 404); any other failure is
  // shown as an error with Retry (it used to look like "No Timeline Generated").
  const { data: timelineData, isLoading: timelineLoading, isError: timelineError, refetch: refetchTimeline } = useQuery({
    queryKey: ['/api/weddings', wedding?.id, 'timeline'],
    queryFn: async () => {
      try {
        const response = await apiRequest(`/api/weddings/${wedding.id}/timeline`);
        const text = await response.text();
        if (!text || text.trim() === '') return EMPTY_TIMELINE;
        return JSON.parse(text);
      } catch (error: any) {
        if (error?.status === 404) return EMPTY_TIMELINE;
        throw error;
      }
    },
    enabled: !!wedding?.id,
    retry: 1,
  });

  const hasTimeline = !!timelineData?.timeline;
  const rawEvents: TimelineEvent[] = Array.isArray(timelineData?.events) ? timelineData.events : [];
  const displayEvents: TimelineEvent[] = coupleView
    ? coupleVisibleEvents(rawEvents, timelineData?.timeline?.startTime)
    : sortEventsByTime(rawEvents, timelineData?.timeline?.startTime);
  const mismatch = timelineMismatch(wedding as any, timelineData?.timeline, rawEvents);
  const weddingDateLabel = weddingDateText(wedding.weddingDate as any);
  const slotLabel = weddingSlotText(wedding as any);
  const packageLabel = useWeddingPackageName(wedding as any);

  // Auto-switch to Day-of Schedule tab if timeline exists
  useEffect(() => {
    if (hasTimeline && !timelineLoading) {
      setActiveTab("timeline");
    }
  }, [hasTimeline, timelineLoading]);

  const openPrintWindow = (html: string) => {
    // Mobile app: a WebView cannot open a blank print window (window.open returns null)
    // and has no print dialog, so the web's "allow pop-ups" advice would be wrong there.
    if (isAppBuild()) {
      toast({
        title: "Printing isn't in the app yet",
        description: "Open your timeline on lamirage.co to print it or save it as a PDF.",
      });
      return;
    }
    // Web: byte-identical to before — openExternal passes the exact window.open arguments.
    const printWindow = openExternal('', '_blank');
    if (!printWindow) {
      toast({
        title: "Pop-up blocked",
        description: "Please allow pop-ups for lamirage.co to print or save the timeline as PDF.",
        variant: "destructive",
      });
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Export functions
  const exportToPrint = () => {
    if (!hasTimeline) return;
    openPrintWindow(generatePrintableTimeline());
  };

  const exportToCards = () => {
    if (!hasTimeline) return;
    openPrintWindow(generateTimelineCards());
  };

  const addToCalendar = () => {
    const ics = buildWeddingIcs(wedding as any);
    if (!ics) {
      toast({ title: "Date not set", description: "Your wedding date isn't available yet.", variant: "destructive" });
      return;
    }
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "wedding-day.ics";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Every value placed into print HTML is escaped: the print window runs on this
  // origin, so unescaped timeline text could run script with the portal's token.
  const generatePrintableTimeline = () => {
    const timeline = timelineData?.timeline;
    const title = escapeHtml(timeline?.timelineTitle || 'Wedding Timeline');

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <meta charset="utf-8">
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #B8860B; padding-bottom: 20px; }
            .header h1 { color: #8a6508; margin: 0; font-size: 28px; }
            .header p { margin: 5px 0; color: #555; }
            .event { margin-bottom: 16px; padding: 12px 15px; border: 1px solid #ddd; border-left: 4px solid #B8860B; break-inside: avoid; }
            .event-header { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; margin-bottom: 6px; }
            .event-title { font-size: 17px; font-weight: bold; color: #222; }
            .event-time { color: #8a6508; font-weight: bold; white-space: nowrap; }
            .detail-item { margin: 4px 0; }
            .detail-label { font-weight: bold; color: #8a6508; }
            .note { font-size: 12px; color: #666; margin-top: 20px; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${title}</h1>
            ${weddingDateLabel ? `<p><strong>Date:</strong> ${escapeHtml(weddingDateLabel)}</p>` : ''}
            ${wedding.venue ? `<p><strong>Venue:</strong> ${escapeHtml(wedding.venue)}</p>` : ''}
            ${packageLabel ? `<p><strong>Package:</strong> ${escapeHtml(packageLabel)}</p>` : ''}
          </div>
          ${displayEvents.map(event => {
            const family = meaningfulText(event.familyInstructions);
            const vendor = meaningfulText(event.vendorInstructions);
            const duration = isPointInTime(event) ? '' : formatDuration(event.duration);
            return `
            <div class="event">
              <div class="event-header">
                <div class="event-title">${escapeHtml(event.eventTitle)}</div>
                <div class="event-time">${escapeHtml(formatEventTime(event))}</div>
              </div>
              ${event.location ? `<div class="detail-item"><span class="detail-label">Where:</span> ${escapeHtml(event.location)}</div>` : ''}
              ${duration ? `<div class="detail-item"><span class="detail-label">Duration:</span> ${escapeHtml(duration)}</div>` : ''}
              ${family ? `<div class="detail-item"><span class="detail-label">For the family:</span> ${escapeHtml(family)}</div>` : ''}
              ${!coupleView && vendor ? `<div class="detail-item"><span class="detail-label">Vendor instructions:</span> ${escapeHtml(vendor)}</div>` : ''}
              ${!coupleView ? `<div class="detail-item"><span class="detail-label">Priority:</span> ${escapeHtml((event.priority || 'medium').toUpperCase())}</div>` : ''}
            </div>`;
          }).join('')}
          <p class="note">All times are Indian Standard Time (IST).</p>
        </body>
      </html>
    `;
  };

  // Staff-only: one card per event for vendors / coordinators.
  const generateTimelineCards = () => {
    const timeline = timelineData?.timeline;
    const title = escapeHtml(timeline?.timelineTitle || 'Wedding Timeline Cards');
    const clip = (v: string | null | undefined) => {
      const t = meaningfulText(v);
      if (!t) return '';
      return escapeHtml(t.length > 80 ? `${t.substring(0, 80)}...` : t);
    };

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Timeline Cards - ${title}</title>
          <meta charset="utf-8">
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            .cards-container { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
            .card { border: 2px solid #B8860B; border-radius: 8px; padding: 15px; background: white; break-inside: avoid; height: 250px; display: flex; flex-direction: column; }
            .card-header { border-bottom: 1px solid #B8860B; padding-bottom: 8px; margin-bottom: 10px; flex-shrink: 0; }
            .card-title { font-size: 16px; font-weight: bold; color: #8a6508; margin: 0; }
            .card-time { font-size: 14px; color: #333; margin: 2px 0; }
            .card-content { flex-grow: 1; overflow: hidden; }
            .card-detail { margin: 8px 0; font-size: 12px; }
            .card-label { font-weight: bold; color: #8a6508; }
            .priority-high { border-color: #dc2626; }
            .priority-medium { border-color: #d97706; }
            .priority-low { border-color: #16a34a; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div style="text-align: center; margin-bottom: 30px;">
            <h1 style="color: #8a6508; margin: 0;">${title}</h1>
            <p style="margin: 5px 0;">${escapeHtml(weddingDateLabel)}${wedding.venue ? ` | ${escapeHtml(wedding.venue)}` : ''}</p>
          </div>
          <div class="cards-container">
            ${displayEvents.map(event => {
              const priority = ['high', 'medium', 'low'].includes(event.priority) ? event.priority : 'medium';
              const duration = isPointInTime(event) ? '' : formatDuration(event.duration);
              const vendor = clip(event.vendorInstructions);
              const family = clip(event.familyInstructions);
              return `
              <div class="card priority-${priority}">
                <div class="card-header">
                  <h3 class="card-title">${escapeHtml(event.eventTitle)}</h3>
                  <div class="card-time">${escapeHtml(formatEventTime(event))}</div>
                </div>
                <div class="card-content">
                  ${event.location ? `<div class="card-detail"><span class="card-label">Location:</span> ${escapeHtml(event.location)}</div>` : ''}
                  ${duration ? `<div class="card-detail"><span class="card-label">Duration:</span> ${escapeHtml(duration)}</div>` : ''}
                  <div class="card-detail"><span class="card-label">Priority:</span> ${escapeHtml(priority.toUpperCase())}</div>
                  ${vendor ? `<div class="card-detail"><span class="card-label">Vendor:</span><br><div style="font-size: 11px; margin-top: 2px;">${vendor}</div></div>` : ''}
                  ${family ? `<div class="card-detail"><span class="card-label">Family:</span><br><div style="font-size: 11px; margin-top: 2px;">${family}</div></div>` : ''}
                </div>
              </div>`;
            }).join('')}
          </div>
        </body>
      </html>
    `;
  };

  // Generate timeline mutation
  const generateTimelineMutation = useMutation({
    mutationFn: async () => {
      if (!wedding?.id) {
        throw new Error('Wedding ID not available');
      }
      const response = await apiRequest('/api/ai/day-of-timeline', {
        method: 'POST',
        data: { weddingId: wedding.id }
      });
      return response.json();
    },
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/weddings', wedding?.id, 'timeline'] });
      // The server returns the saved timeline if one already exists — say so, rather
      // than claiming a new one was created.
      const alreadyExisted = /existing/i.test(result?.message || '');
      toast({
        title: alreadyExisted ? "Your timeline is already prepared" : "Timeline ready",
        description: alreadyExisted
          ? "Showing the saved day-of timeline. Your event manager can update it."
          : "Your day-of timeline has been created.",
      });
      setActiveTab("timeline");
    },
    onError: (error: any) => {
      toast({
        title: "Couldn't create the timeline",
        description: error?.message || "Please try again in a moment.",
        variant: "destructive"
      });
    }
  });

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-900/30 text-red-400 border-red-700';
      case 'medium': return 'bg-yellow-900/30 text-yellow-400 border-yellow-700';
      case 'low': return 'bg-green-900/30 text-green-400 border-green-700';
      default: return 'bg-gray-900/30 text-gray-300 border-gray-700';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch ((category || '').toLowerCase()) {
      case 'ceremony': return '💒';
      case 'reception': return '🎉';
      case 'photography': return '📸';
      case 'catering': return '🍽️';
      case 'decoration': return '🌸';
      case 'pre-event': return '🚗';
      default: return '📋';
    }
  };

  // WhatsApp share mutation
  const shareWhatsAppMutation = useMutation({
    mutationFn: async (phoneNumber: string) => {
      const response = await apiRequest(`/api/weddings/${wedding.id}/timeline/share-whatsapp`, {
        method: 'POST',
        data: { phoneNumber }
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Timeline sent",
        description: "The timeline PDF was sent on WhatsApp.",
      });
      setShareDialogOpen(false);
      setSharePhoneNumber("");
      setSharePhoneError(null);
    },
    onError: (error: any) => {
      toast({
        title: "Couldn't send on WhatsApp",
        description: error?.message || "Please try again in a moment.",
        variant: "destructive"
      });
    }
  });

  // Staff "Regenerate" (review H52): the couple endpoint always returns the saved timeline,
  // so staff generate a PREVIEW (never shown to the couple), check it against the booking,
  // then apply it. The previous version is kept as 'superseded' and can be restored.
  const previewTimelineMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(`/api/admin/weddings/${wedding.id}/timeline/preview`, { method: 'POST' });
      return response.json();
    },
    onSuccess: (result: any) => setRegeneratePreview(result),
    onError: (error: any) => {
      toast({
        title: "Couldn't generate a preview",
        description: error?.message || "Please try again in a moment.",
        variant: "destructive"
      });
    }
  });

  const applyTimelineMutation = useMutation({
    mutationFn: async (previewId: number) => {
      const response = await apiRequest(`/api/admin/weddings/${wedding.id}/timeline/apply`, {
        method: 'POST',
        data: { previewId: previewId }
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/weddings', wedding?.id, 'timeline'] });
      setRegeneratePreview(null);
      toast({ title: "Timeline replaced", description: "The new timeline is now the current one. The previous version was kept." });
      setActiveTab("timeline");
    },
    onError: (error: any) => {
      toast({
        title: "Couldn't apply the preview",
        description: error?.message || "Nothing was changed. Please try again.",
        variant: "destructive"
      });
    }
  });

  const handleShareWhatsApp = () => {
    if (!sharePhoneNumber.trim()) {
      setSharePhoneError("Please enter a WhatsApp number.");
      return;
    }
    if (!isValidSharePhone(sharePhoneNumber)) {
      setSharePhoneError("Enter the number with country code, e.g. +91 98765 43210.");
      return;
    }
    setSharePhoneError(null);
    shareWhatsAppMutation.mutate(sharePhoneNumber.trim());
  };

  const goldButton = "bg-[#B8860B] hover:bg-[#9A7209] text-black";
  const outlineButton = "text-[#B8860B] border-[#B8860B] hover:bg-[#B8860B]/10 min-h-11 h-11";

  const scheduleActions = (
    <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 w-full sm:w-auto">
      <Button onClick={exportToPrint} variant="outline" className={`${outlineButton} text-sm`}>
        <Printer className="h-4 w-4 mr-2" />
        Print / PDF
      </Button>
      <Button onClick={addToCalendar} variant="outline" className={`${outlineButton} text-sm`}>
        <CalendarPlus className="h-4 w-4 mr-2" />
        Add to calendar
      </Button>
      <Dialog
        open={shareDialogOpen}
        onOpenChange={(open) => {
          setShareDialogOpen(open);
          if (!open) setSharePhoneError(null);
        }}
      >
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="text-green-400 border-green-500 hover:bg-green-500/10 min-h-11 h-11 text-sm"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            WhatsApp
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-gray-900 border-[#B8860B]/30">
          <DialogHeader>
            <DialogTitle className="text-white">Send timeline on WhatsApp</DialogTitle>
            <DialogDescription className="text-gray-300">
              LA Mirage will send a PDF of this timeline to the WhatsApp number below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="timeline-share-phone" className="text-gray-300">WhatsApp number</Label>
            <Input
              id="timeline-share-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="+91 98765 43210"
              value={sharePhoneNumber}
              onChange={(e) => {
                setSharePhoneNumber(e.target.value);
                if (sharePhoneError) setSharePhoneError(null);
              }}
              aria-invalid={!!sharePhoneError}
              aria-describedby="timeline-share-phone-help"
              className="bg-black/40 border-[#B8860B]/30 text-white h-11"
            />
            <p id="timeline-share-phone-help" className={`text-xs ${sharePhoneError ? 'text-red-400' : 'text-gray-400'}`}>
              {sharePhoneError || "Include the country code (+91 for India, +971 for UAE)."}
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={handleShareWhatsApp}
              disabled={shareWhatsAppMutation.isPending}
              className="bg-green-600 hover:bg-green-700 text-white min-h-11"
            >
              {shareWhatsAppMutation.isPending ? "Sending..." : "Send on WhatsApp"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {!coupleView && (
        <Button onClick={exportToCards} variant="outline" className={`${outlineButton} text-sm`}>
          <Printer className="h-4 w-4 mr-2" />
          Print cards
        </Button>
      )}
    </div>
  );

  const mismatchBanner = hasTimeline && (mismatch.date || mismatch.time) ? (
    <div role="status" className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 sm:p-4 text-sm text-amber-100">
      <div className="flex gap-2">
        <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          {coupleView ? (
            <>
              <p className="font-medium text-amber-50">This is a draft schedule</p>
              <p className="mt-1">
                It was prepared for {mismatch.date ? 'an earlier date' : 'a different time'} than your booking
                {weddingDateLabel ? ` (${weddingDateLabel}${slotLabel ? `, ${slotLabel}` : ''})` : ''}.
                Your event manager will update it before your wedding.
              </p>
            </>
          ) : (
            <p>
              Saved timeline does not match the booking:
              {mismatch.date && ` timeline date ${weddingDateText(timelineData?.timeline?.weddingDate) || 'unknown'} vs booking ${weddingDateLabel || 'unknown'};`}
              {mismatch.time && ` events fall outside the booked slot${slotLabel ? ` (${slotLabel})` : ''}.`}
            </p>
          )}
        </div>
      </div>
    </div>
  ) : null;

  const schedule = (
    <div className="space-y-4">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-[#B8860B] break-words min-w-0">
            {timelineData?.timeline?.timelineTitle}
          </h3>
          <Badge variant="outline" className="text-[#B8860B] border-[#B8860B] text-xs">
            {displayEvents.length} {displayEvents.length === 1 ? 'moment' : 'moments'}
          </Badge>
        </div>
        {scheduleActions}
      </div>

      {mismatchBanner}

      {displayEvents.length === 0 ? (
        <Card className="bg-black/40 border-[#B8860B]/30">
          <CardContent className="p-6 text-center text-gray-300">
            The schedule has no events yet. Your event manager will add them.
          </CardContent>
        </Card>
      ) : (
        <ol className="space-y-3">
          {displayEvents.map((event) => {
            const point = isPointInTime(event);
            const duration = point ? '' : formatDuration(event.duration);
            const family = meaningfulText(event.familyInstructions);
            const vendor = meaningfulText(event.vendorInstructions);
            const description = meaningfulText(event.eventDescription);
            const showDescription = description && description !== event.eventTitle;
            const staff = Array.isArray(event.requiredStaff) ? event.requiredStaff.map(humanizeSlug).filter(Boolean) : [];
            return (
              <li key={event.id}>
                <Card className="bg-black/40 border-[#B8860B]/30 border-l-4 border-l-[#B8860B] overflow-hidden">
                  <CardContent className="p-3 sm:p-4">
                    <div className="space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                        <div className="flex items-start gap-2 min-w-0 flex-1">
                          <span className="text-base sm:text-lg flex-shrink-0" aria-hidden="true">{getCategoryIcon(event.category)}</span>
                          <h4 className="font-semibold text-white text-sm sm:text-base break-words">{event.eventTitle}</h4>
                          {!coupleView && event.priority && (
                            <Badge className={`${getPriorityColor(event.priority)} text-xs flex-shrink-0`}>
                              {event.priority}
                            </Badge>
                          )}
                        </div>
                        <Badge variant="outline" className="text-xs self-start text-[#B8860B] border-[#B8860B]/50">
                          {categoryLabel(event.category)}
                        </Badge>
                      </div>

                      {showDescription && (
                        <p className="text-gray-300 text-xs sm:text-sm break-words">{description}</p>
                      )}

                      <div className="space-y-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <Clock className="h-4 w-4 text-[#B8860B] flex-shrink-0" />
                          <span className="text-xs sm:text-sm font-medium flex-shrink-0 text-gray-300">Time:</span>
                          <span className="text-xs sm:text-sm text-white">{formatEventTime(event)}</span>
                        </div>
                        {event.location && (
                          <div className="flex items-center gap-2 min-w-0">
                            <MapPin className="h-4 w-4 text-[#B8860B] flex-shrink-0" />
                            <span className="text-xs sm:text-sm font-medium flex-shrink-0 text-gray-300">Where:</span>
                            <span className="text-xs sm:text-sm text-white break-words min-w-0">{event.location}</span>
                          </div>
                        )}
                        {duration && (
                          <div className="flex items-center gap-2 min-w-0">
                            <Timer className="h-4 w-4 text-[#B8860B] flex-shrink-0" />
                            <span className="text-xs sm:text-sm font-medium flex-shrink-0 text-gray-300">Duration:</span>
                            <span className="text-xs sm:text-sm text-white">{duration}</span>
                          </div>
                        )}
                        {!coupleView && staff.length > 0 && (
                          <div className="flex items-start gap-2 min-w-0">
                            <Users className="h-4 w-4 text-[#B8860B] flex-shrink-0 mt-0.5" />
                            <span className="text-xs sm:text-sm font-medium flex-shrink-0 text-gray-300">Staff:</span>
                            <span className="text-xs sm:text-sm text-white break-words min-w-0">{staff.join(', ')}</span>
                          </div>
                        )}
                      </div>

                      {(family || (!coupleView && vendor)) && (
                        <div className="space-y-3">
                          {!coupleView && vendor && (
                            <div className="overflow-hidden">
                              <span className="font-medium text-[#B8860B] text-xs sm:text-sm">Vendor Instructions:</span>
                              <p className="text-gray-300 mt-1 text-xs sm:text-sm break-words">{vendor}</p>
                            </div>
                          )}
                          {family && (
                            <div className="overflow-hidden">
                              <span className="font-medium text-[#B8860B] text-xs sm:text-sm">
                                {coupleView ? 'For your family:' : 'Family Instructions:'}
                              </span>
                              <p className="text-gray-300 mt-1 text-xs sm:text-sm break-words">{family}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ol>
      )}

      <p className="text-xs text-gray-400">All times are Indian Standard Time (IST).</p>

      {coupleView && (
        <Card className="bg-black/40 border-[#B8860B]/30">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-gray-300">Need a change to the schedule? Your event manager can update it.</p>
            <Link href="/support">
              <Button variant="outline" className={`${outlineButton} w-full sm:w-auto`}>
                <LifeBuoy className="h-4 w-4 mr-2" />
                Ask my event manager
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const loadingCard = (
    <Card className="bg-black/40 border-[#B8860B]/30">
      <CardContent className="p-6">
        <div className="flex items-center justify-center" role="status">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B8860B]" />
          <span className="ml-2 text-gray-300">Loading timeline...</span>
        </div>
      </CardContent>
    </Card>
  );

  const errorCard = (
    <Card className="bg-black/40 border-[#B8860B]/30">
      <CardContent className="p-6 text-center">
        <AlertCircle className="h-10 w-10 text-amber-400 mx-auto mb-3" />
        <h3 className="font-semibold text-white mb-2">We couldn't load your timeline</h3>
        <p className="text-gray-300 mb-4 text-sm">Please check your connection and try again.</p>
        <Button onClick={() => refetchTimeline()} className={`${goldButton} min-h-11`}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Try again
        </Button>
      </CardContent>
    </Card>
  );

  const generateCard = (
    <Card className="bg-black/40 border-[#B8860B]/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <Star className="h-5 w-5 text-[#B8860B]" />
          Wedding Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-hidden">
          {wedding.guestCount ? (
            <div className="flex items-center gap-2 min-w-0">
              <Users className="h-4 w-4 text-[#B8860B] flex-shrink-0" />
              <span className="text-sm font-medium flex-shrink-0 text-gray-300">Guest Count:</span>
              <span className="text-sm truncate text-white">{Number(wedding.guestCount).toLocaleString('en-IN')}</span>
            </div>
          ) : null}
          {weddingDateLabel && (
            <div className="flex items-center gap-2 min-w-0">
              <Calendar className="h-4 w-4 text-[#B8860B] flex-shrink-0" />
              <span className="text-sm font-medium flex-shrink-0 text-gray-300">Date:</span>
              <span className="text-sm truncate text-white">{weddingDateLabel}</span>
            </div>
          )}
          {slotLabel && (
            <div className="flex items-center gap-2 min-w-0">
              <Clock className="h-4 w-4 text-[#B8860B] flex-shrink-0" />
              <span className="text-sm font-medium flex-shrink-0 text-gray-300">Time:</span>
              <span className="text-sm truncate text-white">{slotLabel}</span>
            </div>
          )}
          {wedding.venue && (
            <div className="flex items-center gap-2 min-w-0">
              <MapPin className="h-4 w-4 text-[#B8860B] flex-shrink-0" />
              <span className="text-sm font-medium flex-shrink-0 text-gray-300">Venue:</span>
              <span className="text-sm truncate text-white">{wedding.venue}</span>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-r from-[#B8860B]/20 to-gray-900/50 p-4 sm:p-6 rounded-lg border border-[#B8860B]/30">
          <h3 className="font-semibold text-[#B8860B] mb-3 text-sm sm:text-base">What you get:</h3>
          <ul className="grid grid-cols-1 gap-2 sm:gap-3 overflow-hidden">
            {(coupleView
              ? ['A running order for your wedding day', 'Timings in IST for your booked slot', 'Notes for your family at each moment', 'Printable and shareable on WhatsApp']
              : ['Package-based service allocation', 'Employee availability integration', 'Venue logistics coordination', 'Contingency planning', 'IST time formatting', 'Printable timeline cards']
            ).map((label) => (
              <li key={label} className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span className="text-sm text-gray-300">{label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex justify-center">
          <Button
            onClick={() => generateTimelineMutation.mutate()}
            disabled={generateTimelineMutation.isPending}
            size="lg"
            className={`${goldButton} px-8 min-h-11`}
          >
            {generateTimelineMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2" />
                Preparing your timeline... this can take a minute
              </>
            ) : (
              <>
                <Wand2 className="h-5 w-5 mr-2" />
                Generate Event Timeline
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Staff-only preview dialog for "Regenerate" (rendered only in the staff view below).
  const previewEvents: TimelineEvent[] = Array.isArray(regeneratePreview?.preview?.events)
    ? sortEventsByTime(regeneratePreview.preview.events, regeneratePreview?.preview?.timeline?.startTime)
    : [];
  const previewWarningsList: string[] = Array.isArray(regeneratePreview?.warnings) ? regeneratePreview.warnings : [];
  const bookedWindow = regeneratePreview?.booking?.window;
  const regenerateDialog = (
    <Dialog open={!!regeneratePreview} onOpenChange={(open) => { if (!open) setRegeneratePreview(null); }}>
      <DialogContent className="bg-gray-900 border-[#B8860B]/40 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#B8860B]">Preview: new timeline from the booking</DialogTitle>
          <DialogDescription className="text-gray-300">
            Staff only. The couple keeps seeing the current timeline until you apply this preview.
            {bookedWindow ? ` Booked time: ${bookedWindow.startTime}-${bookedWindow.endTime} IST.` : " The booking has no time slot."}
          </DialogDescription>
        </DialogHeader>
        {previewWarningsList.length > 0 && (
          <div className="rounded-lg border border-yellow-700 bg-yellow-900/30 p-3 text-sm text-yellow-300 space-y-1">
            {previewWarningsList.map((w) => (
              <div key={w} className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{w}</span>
              </div>
            ))}
          </div>
        )}
        <ul className="divide-y divide-[#B8860B]/10">
          {previewEvents.map((e) => (
            <li key={e.id} className="py-2 flex gap-3 text-sm">
              <span className="w-28 flex-shrink-0 text-[#B8860B]">{formatEventTime(e)}</span>
              <span className="text-white">{e.eventTitle}</span>
            </li>
          ))}
        </ul>
        <DialogFooter className="gap-2">
          <Button variant="outline" className={outlineButton} onClick={() => setRegeneratePreview(null)}>
            Keep current timeline
          </Button>
          <Button
            className={`${goldButton} min-h-11`}
            disabled={applyTimelineMutation.isPending || !regeneratePreview?.preview?.timeline?.id || previewEvents.length === 0}
            onClick={() => applyTimelineMutation.mutate(regeneratePreview.preview.timeline.id)}
          >
            {applyTimelineMutation.isPending ? "Applying..." : "Apply this timeline"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // ---------------------------------------------------------------------------
  // Couple view: no tabs. Schedule when it exists; otherwise the generate card.
  // (Regenerate is hidden — the server always returns the saved timeline anyway.)
  // ---------------------------------------------------------------------------
  if (coupleView) {
    return (
      <div className="space-y-6">
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-bold text-[#B8860B] flex items-center gap-2">
            <Clock className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
            <span>Your Day-of Timeline</span>
          </h2>
          <p className="text-gray-300 mt-1 text-sm sm:text-base">
            The running order for your wedding day, moment by moment.
          </p>
        </div>
        {timelineLoading ? loadingCard : timelineError ? errorCard : hasTimeline ? schedule : generateCard}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Staff view (admin/event manager pages): unchanged tabs, full run sheet.
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl sm:text-2xl font-bold text-[#B8860B] flex items-center gap-2">
            <Wand2 className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
            <span className="truncate">AI Event Timeline</span>
          </h2>
          <p className="text-gray-300 mt-1 text-sm sm:text-base">
            Generate intelligent venue-specific wedding day schedules
          </p>
        </div>
        {hasTimeline && (
          <Button
            onClick={() => previewTimelineMutation.mutate()}
            disabled={previewTimelineMutation.isPending || applyTimelineMutation.isPending}
            className={`${goldButton} text-sm min-h-11 h-11`}
          >
            <Zap className="h-4 w-4 mr-2" />
            {previewTimelineMutation.isPending ? "Generating preview..." : "Regenerate"}
          </Button>
        )}
      </div>
      {regenerateDialog}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 h-auto bg-black/60 border border-[#B8860B]/30">
          <TabsTrigger value="generate" className="min-h-11 data-[state=active]:bg-[#B8860B] data-[state=active]:text-black text-gray-300">Generate Timeline</TabsTrigger>
          <TabsTrigger value="timeline" className="min-h-11 data-[state=active]:bg-[#B8860B] data-[state=active]:text-black text-gray-300">Day-of Schedule</TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="space-y-6">
          {generateCard}
        </TabsContent>

        <TabsContent value="timeline" className="space-y-4">
          {timelineLoading && loadingCard}
          {!timelineLoading && timelineError && errorCard}
          {!timelineLoading && !timelineError && hasTimeline && schedule}
          {!timelineLoading && !timelineError && !hasTimeline && (
            <Card className="bg-black/40 border-[#B8860B]/30">
              <CardContent className="p-6 text-center">
                <AlertCircle className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                <h3 className="font-semibold text-white mb-2">No Timeline Generated</h3>
                <p className="text-gray-300 mb-4">
                  Generate an AI-powered timeline to see the wedding day schedule
                </p>
                <Button
                  onClick={() => setActiveTab("generate")}
                  className={`${goldButton} min-h-11`}
                >
                  <Wand2 className="h-4 w-4 mr-2" />
                  Generate Timeline
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
